import React from "react";

import { formatDateOnly } from "../../../utils/datetime";
import { TenancyStatusCell } from "../tenants/TenantUnitGroup";
import TenancyRowActions from "../tenants/TenancyRowActions";
import { formatChf } from "../tenants/tenantOverviewData";

/**
 * One row per tenancy — the flat central list.
 *
 * Deliberately NOT grouped by unit: this page answers "show me the contracts",
 * and grouping buries the reference number and the lifecycle dates that the
 * question is actually about. `/admin/tenants` keeps the grouped view.
 *
 * Every cell reads a field the LIST response already carries, so the table
 * costs exactly one request per page. Nothing here fetches per row.
 *
 * The status badge is imported rather than re-implemented: the admin wording
 * ("Vor Einzug storniert") and the tone map live in one place, next to the
 * shared `tenancyStatusLabelDe` they delegate to. A second copy would drift the
 * first time a status is added to one of them.
 */

const HEADERS = [
  "Referenz",
  "Mieter",
  "Einheit / Zimmer",
  "Status",
  "Mietbeginn",
  "Mietende",
  "Monatsmiete",
  "Kaution",
  "",
];

const CELL = "px-[14px] py-[11px] align-middle text-[13px]";

function TenantCell({ primary, coTenants }) {
  const name = primary?.name || "—";
  const extra = Array.isArray(coTenants) ? coTenants.length : 0;
  return (
    <div className="min-w-0">
      <p className="m-0 truncate font-medium text-foreground">{name}</p>
      {extra > 0 ? (
        <p className="m-0 truncate text-[11.5px] text-muted-foreground">
          +{extra} {extra === 1 ? "Co-Mieter" : "Co-Mieter"}
        </p>
      ) : null}
    </div>
  );
}

/** Unit over room — the room line is omitted when the tenancy has no room. */
function UnitCell({ unitTitle, unitShortId, roomName }) {
  const top = unitTitle || unitShortId || "—";
  return (
    <div className="min-w-0">
      <p className="m-0 truncate text-foreground">{top}</p>
      {roomName ? (
        <p className="m-0 truncate text-[11.5px] text-muted-foreground">
          {roomName}
        </p>
      ) : null}
    </div>
  );
}

export default function TenancyTable({
  rows,
  selectedId,
  onSelect,
  buildActions,
}) {
  return (
    <div className="overflow-x-auto rounded-[10px] border border-border bg-card">
      <table className="w-full border-collapse text-left">
        <thead>
          <tr className="border-b border-border bg-muted/40">
            {HEADERS.map((h, i) => (
              <th
                key={h || `actions-${i}`}
                scope="col"
                className="whitespace-nowrap px-[14px] py-[9px] text-[11px] font-medium uppercase tracking-[0.04em] text-muted-foreground"
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const selected = selectedId != null && row.id === selectedId;
            return (
              <tr
                key={row.id}
                onClick={() => onSelect(row)}
                aria-selected={selected}
                data-testid={`tenancy-row-${row.id}`}
                className={`cursor-pointer border-b border-border/70 last:border-b-0 transition-colors ${
                  selected
                    ? "bg-primary/[0.07] shadow-[inset_3px_0_0_0_hsl(var(--primary))]"
                    : "hover:bg-muted/50"
                }`}
              >
                <td className={`${CELL} whitespace-nowrap font-medium text-foreground`}>
                  {row.referenceNumber || "—"}
                </td>
                <td className={CELL}>
                  <TenantCell primary={row.primary} coTenants={row.coTenants} />
                </td>
                <td className={CELL}>
                  <UnitCell
                    unitTitle={row.unitTitle}
                    unitShortId={row.unitShortId}
                    roomName={row.roomName}
                  />
                </td>
                {/* Sometimes two pills: a terminated contract whose effective
                    end is still ahead is "Aktiv" AND "Gekündigt", and the
                    "Aktiv" filter returns it. The DATE that termination takes
                    effect is not repeated here — it is already this row's
                    Mietende, two columns along. */}
                <td className={`${CELL} whitespace-nowrap`}>
                  <TenancyStatusCell
                    displayStatus={row.displayStatus}
                    isRunning={row.isRunning}
                  />
                </td>
                <td className={`${CELL} whitespace-nowrap tabular-nums text-foreground`}>
                  {formatDateOnly(row.moveInDate)}
                </td>
                <td className={`${CELL} whitespace-nowrap tabular-nums text-foreground`}>
                  {formatDateOnly(row.displayEndDate)}
                </td>
                {/* `revenueEquivalent` is the tenancy's monthly revenue
                    equivalent from TenancyRevenue, already gated on
                    revenue-bearing statuses by the shared viewmodel. The
                    deprecated `monthly_rent` column is deliberately not read. */}
                <td className={`${CELL} whitespace-nowrap tabular-nums text-foreground`}>
                  {row.revenueEquivalent == null
                    ? "—"
                    : formatChf(row.revenueEquivalent)}
                </td>
                <td className={`${CELL} whitespace-nowrap tabular-nums text-foreground`}>
                  {row.depositAmount == null ? "—" : formatChf(row.depositAmount)}
                </td>
                <td
                  className={`${CELL} w-[52px] text-right`}
                  onClick={(e) => e.stopPropagation()}
                >
                  <TenancyRowActions items={buildActions(row)} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
